<template>
  <div class="dtb">
    <div class="dtb__board">
      <desk-picker
        v-model="desk.template"
        type="statusTemplate"
        :placeholder="$t('taskDeskTemplate')"
        :set-init="true"
      />
    </div>
    <div class="dtb__search">
      <v-text-field
        v-model="desk.search"
        :placeholder="$t('search')"
        prepend-inner-icon="mdi-magnify"
        clearable
        outlined
        dense
        hide-details
      />
    </div>

    <div class="dtb__views" role="tablist">
      <button
        v-for="v in viewList"
        :key="v.id"
        type="button"
        role="tab"
        class="dtb__view"
        :class="{ 'dtb__view--on': desk.view === v.id }"
        :aria-selected="desk.view === v.id"
        @click="desk.view = v.id"
      >
        <v-icon small>{{ v.icon }}</v-icon>
        <span>{{ $t(v.label) }}</span>
      </button>
    </div>

    <v-spacer />

    <span v-if="desk.template" class="dtb__count">
      {{ desk.totalTasks }} {{ $t('task') }}
    </span>
    <button
      v-shortkey="['f5']"
      type="button"
      class="dtb__icon"
      :title="$t('refresh')"
      @click="desk.getItems()"
      @shortkey="desk.getItems()"
    >
      <v-icon small :class="{ 'dtb__spin': desk.isLoading }">mdi-refresh</v-icon>
    </button>
    <button
      type="button"
      class="dtb__btn"
      :class="{ 'dtb__btn--active': desk.activeFilterCount }"
      @click="desk.filterOpen = true"
    >
      <v-icon small>mdi-filter-variant</v-icon>
      <span>{{ $t('filter') }}</span>
      <span v-if="desk.activeFilterCount" class="dtb__badge">{{ desk.activeFilterCount }}</span>
    </button>
    <v-btn
      color="primary"
      depressed
      height="38"
      class="dtb__new"
      :disabled="!desk.template"
      @click="desk.openComponent('task', null)"
    >
      <v-icon left small>mdi-plus</v-icon>
      {{ $t('task') }}
    </v-btn>
  </div>
</template>

<script>
import DeskPicker from '../../setting/components/SettingAccountPicker'

export const VIEW_LIST = [
  { id: 'board', icon: 'mdi-view-column-outline', label: 'taskDeskViewBoard' },
  { id: 'list', icon: 'mdi-format-list-bulleted', label: 'taskDeskViewList' },
  { id: 'gantt', icon: 'mdi-chart-gantt', label: 'taskDeskViewGantt' },
  { id: 'calendar', icon: 'mdi-calendar-month-outline', label: 'taskDeskViewCalendar' },
]

export default {
  name: 'DeskToolbar',
  components: { DeskPicker },
  inject: ['desk'],
  computed: {
    viewList () {
      return VIEW_LIST
    },
  },
}
</script>

<style scoped>
.dtb {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
}
.dtb__board {
  flex: 0 1 230px;
  min-width: 150px;
}
.dtb__search {
  flex: 0 1 220px;
  min-width: 110px;
}
.dtb ::v-deep .v-text-field--outlined.v-input--dense .v-input__slot {
  min-height: 40px !important;
  background: #fff;
}
.theme--dark .dtb ::v-deep .v-text-field--outlined.v-input--dense .v-input__slot {
  background: #1e1e1e;
}
.dtb__views {
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  background: #e6e9f0;
  border-radius: 8px;
}
.theme--dark .dtb__views {
  background: #2a2a2a;
}
.dtb__view {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 0.82rem;
  color: #5b6475;
  transition: background 0.15s, color 0.15s;
}
.dtb__view .v-icon {
  color: inherit;
}
.dtb__view:hover {
  color: #1f2937;
}
.dtb__view--on {
  background: #fff;
  color: var(--v-primary-base);
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}
.theme--dark .dtb__view--on {
  background: #1e1e1e;
}
.dtb__count {
  font-size: 0.8rem;
  color: #8a94a6;
  margin-inline-end: 4px;
}
.dtb__icon,
.dtb__btn {
  height: 38px;
  border: 1px solid #dfe3ea;
  background: #fff;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #4b5563;
  transition: border-color 0.15s;
}
.theme--dark .dtb__icon,
.theme--dark .dtb__btn {
  background: #1e1e1e;
  border-color: #3a3a3a;
  color: #ddd;
}
.dtb__icon .v-icon,
.dtb__btn .v-icon {
  color: inherit;
}
.dtb__icon {
  width: 38px;
}
.dtb__btn {
  padding: 0 12px;
  font-size: 0.85rem;
}
.dtb__icon:hover,
.dtb__btn:hover {
  border-color: #b8c0cc;
}
.dtb__btn--active {
  border-color: var(--v-primary-base);
  color: var(--v-primary-base);
}
.dtb__badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--v-primary-base);
  color: #fff;
  font-size: 0.7rem;
  line-height: 18px;
  text-align: center;
}
.dtb__new {
  border-radius: 8px;
}
/* narrow desktops: icon-only view switcher and buttons */
@media (max-width: 1180px) {
  .dtb__view span,
  .dtb__btn > span:not(.dtb__badge),
  .dtb__count {
    display: none;
  }
  .dtb__view {
    padding: 0 10px;
  }
}
.dtb__spin {
  animation: dtb-spin 0.8s linear infinite;
}
@keyframes dtb-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
